import { findPeriodByName, normalizePeriodName } from './scheduleTargetUtils';

export const PERMANENT_START_TIMES = {
  bloodPressure: {
    'Wake-up': '05:00 AM',
    Morning: '08:00 AM',
    Noon: '11:00 AM',
    Afternoon: '02:00 PM',
    Evening: '05:00 PM'
  },
  bloodGlucose: {
    'Wake-up Time': '06:00 AM',
    'Before-Breakfast': '07:00 AM',
    'After-Breakfast': '08:00 AM',
    'Before-Lunch': '11:00 AM',
    'After-Lunch': '01:00 PM',
    'Before-Dinner': '05:00 PM',
    'After-Dinner': '07:00 PM',
    Bedtime: '08:30 PM',
    Midnight: '10:00 PM'
  },
  weight: {
    Morning: '06:00 AM',
    Afternoon: '12:00 PM',
    Evening: '06:00 PM'
  }
};

export const PERMANENT_END_TIMES = {
  bloodPressure: {
    'Wake-up': '07:59 AM',
    Morning: '10:59 AM',
    Noon: '01:59 PM',
    Afternoon: '04:59 PM',
    Evening: '04:59 AM'
  },
  bloodGlucose: {
    'Wake-up Time': '06:59 AM',
    'Before-Breakfast': '07:59 AM',
    'After-Breakfast': '10:59 AM',
    'Before-Lunch': '12:59 PM',
    'After-Lunch': '04:59 PM',
    'Before-Dinner': '06:59 PM',
    'After-Dinner': '08:29 PM',
    Bedtime: '09:59 PM',
    Midnight: '05:59 AM'
  },
  weight: {
    Morning: '11:59 AM',
    Afternoon: '05:59 PM',
    Evening: '05:59 AM'
  }
};

export const convertTimeToMinutes = (timeStr) => {
  if (!timeStr) return null;
  if (timeStr.includes('AM') || timeStr.includes('PM')) {
    const [time, meridiem] = timeStr.trim().split(' ');
    const [hh, mm] = time.split(':').map(Number);
    if (!Number.isFinite(hh) || !Number.isFinite(mm)) return null;
    let hour = hh;
    if (meridiem?.toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (meridiem?.toUpperCase() === 'AM' && hour === 12) hour = 0;
    return hour * 60 + mm;
  }
  const [h, m] = timeStr.split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
  return h * 60 + m;
};

export const getCurrentTimeHHmm = () => new Date().toTimeString().slice(0, 5);

export const getUniquePeriods = (periods = []) => {
  const seen = new Set();
  return (Array.isArray(periods) ? periods : []).filter((period) => {
    const key = normalizePeriodName(period?.name);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const getInstantPeriodOptions = (periods = [], permanentStartTimes = {}) => {
  const uniqueFetched = getUniquePeriods(periods);
  if (uniqueFetched.length > 0) return uniqueFetched;
  return Object.keys(permanentStartTimes || {}).map((name) => ({ id: null, name }));
};

export function resolvePermanentTime(periodName, timeMap = {}) {
  if (!periodName) return '';
  if (timeMap[periodName]) return timeMap[periodName];
  const normalized = normalizePeriodName(periodName);
  const matchedKey = Object.keys(timeMap).find((key) => normalizePeriodName(key) === normalized);
  return matchedKey ? timeMap[matchedKey] : '';
}

/** @deprecated use resolvePermanentTime */
export const resolvePermanentStart = resolvePermanentTime;

/**
 * Build contiguous period windows from configured starts.
 * Permanent start/end maps override API values when present.
 */
export function buildPeriodTimeWindows(
  periods = [],
  permanentStartTimes = {},
  permanentEndTimes = {}
) {
  const unique = getUniquePeriods(periods);
  const sourceList =
    unique.length > 0
      ? unique
      : Object.keys(permanentStartTimes || {}).map((name) => ({ name }));

  const entries = sourceList
    .map((period) => {
      const startTime =
        resolvePermanentTime(period?.name, permanentStartTimes) ||
        period?.startTime ||
        period?.start ||
        '';
      const endTime =
        resolvePermanentTime(period?.name, permanentEndTimes) ||
        period?.endTime ||
        period?.end ||
        '';
      const startMin = convertTimeToMinutes(startTime);
      if (startMin == null) return null;
      const explicitEndMin = convertTimeToMinutes(endTime);
      return {
        name: period.name,
        startMin,
        explicitEndMin
      };
    })
    .filter(Boolean);

  if (entries.length === 0) return [];

  entries.sort((a, b) => a.startMin - b.startMin);

  return entries.map((entry, index) => {
    const next = entries[(index + 1) % entries.length];
    let endMin = entry.explicitEndMin;
    if (endMin == null) {
      endMin = next.startMin - 1;
      if (endMin < 0) endMin += 1440;
    }
    const isOvernight = endMin < entry.startMin;
    return {
      name: entry.name,
      startMin: entry.startMin,
      endMin,
      isOvernight
    };
  });
}

export const determinePeriodFromTime = (
  timeStr,
  periods = [],
  permanentStartTimes = {},
  permanentEndTimes = {}
) => {
  const current = convertTimeToMinutes(timeStr);
  if (current == null) return null;

  const windows = buildPeriodTimeWindows(periods, permanentStartTimes, permanentEndTimes);
  if (windows.length === 0) return null;

  const matches = windows
    .map((window) => {
      const { startMin, endMin, isOvernight } = window;
      const inRange = isOvernight
        ? current >= startMin || current <= endMin
        : current >= startMin && current <= endMin;
      if (!inRange) return null;

      const duration = isOvernight ? 1440 - startMin + endMin : endMin - startMin;
      return {
        name: window.name,
        isOvernight,
        duration
      };
    })
    .filter(Boolean);

  if (matches.length === 0) return null;

  matches.sort((a, b) => {
    if (a.isOvernight !== b.isOvernight) return a.isOvernight ? 1 : -1;
    return a.duration - b.duration;
  });

  return matches[0]?.name || null;
};

export const determinePeriodFromCurrentTime = (
  periods = [],
  permanentStartTimes = {},
  permanentEndTimes = {}
) => determinePeriodFromTime(getCurrentTimeHHmm(), periods, permanentStartTimes, permanentEndTimes);

export const formatTimeDisplay12h = (time) => {
  if (!time) return '';
  if (time.includes('AM') || time.includes('PM')) return time;
  const [h, m] = time.split(':');
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour % 12 || 12;
  return `${displayHour}:${m || '00'} ${period}`;
};

export function getPermanentPeriodDisplay(
  periodName,
  permanentStartTimes = {},
  permanentEndTimes = {},
  apiPeriod = null
) {
  const startTime =
    resolvePermanentTime(periodName, permanentStartTimes) ||
    resolvePermanentTime(apiPeriod?.name, permanentStartTimes) ||
    apiPeriod?.startTime ||
    apiPeriod?.start ||
    '';
  const endTime =
    resolvePermanentTime(periodName, permanentEndTimes) ||
    resolvePermanentTime(apiPeriod?.name, permanentEndTimes) ||
    apiPeriod?.endTime ||
    apiPeriod?.end ||
    '';
  return {
    startTime,
    endTime,
    startTimeDisplay: formatTimeDisplay12h(startTime),
    endTimeDisplay: formatTimeDisplay12h(endTime)
  };
}
